<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <div class="card card-block">
          <div class="card-body p-2">
            <!-- HEADER -->
            <div class="d-flex justify-content-between align-items-start p-2">
              <b-jumbotron lead="Reporte de acceso"></b-jumbotron>
            </div>

            <!-- ALERT INFO -->
            <b-alert class="p-3" show variant="info" dismissible>
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor" class="bi bi-info-circle-fill mr-2" viewBox="0 0 16 16">
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
              </svg>
              En esta sección puede visualizar la última conexión de los usuarios activos por escuela.
            </b-alert>

            <!-- FILTROS -->
            <b-row class="my-3 mx-2">
              <b-col cols="12" md="4" 
               v-if="$store.getters.usuario && $store.getters.usuario.roles && ($store.getters.usuario.roles.some(r => r.name === 'admin') || $store.getters.usuario.roles.some(r => r.name === 'docente'))">
                <label><small>Escuela/Dominio</small></label>
                <v-select
                    v-model="filtros.escuela"
                    :options="filtros.escuelas_items"
                    placeholder="Seleccionar escuela"
                    @input="aplicarFiltros()"
                >
                </v-select>
              </b-col>

              <b-col cols="12" md="3">
                <label><small>Fecha inicio</small></label>
                <b-form-datepicker 
                    v-model="filtros.fecha_inicio"
                    locale="es"
                    placeholder="Fecha inicio"
                    size="sm"
                    label-help=""
                    label-today-button="Hoy"
                    today-button
                    label-no-date-selected="No se ha elegido fecha"
                    @input="aplicarFiltros()"
                >
                </b-form-datepicker>
              </b-col>

              <b-col cols="12" md="3">
                <label><small>Fecha fin</small></label>
                <b-form-datepicker 
                    v-model="filtros.fecha_fin"
                    locale="es"
                    placeholder="Fecha fin"
                    size="sm"
                    label-help=""
                    label-today-button="Hoy"
                    today-button
                    label-no-date-selected="No se ha elegido fecha"
                    @input="aplicarFiltros()"
                >
                </b-form-datepicker>
              </b-col>

              <b-col cols="12" md="2" class="d-flex align-items-end">
                <b-button 
                    @click="limpiarFiltros()" 
                    variant="outline-secondary" 
                    size="sm"
                    block
                >
                  Limpiar filtros
                </b-button>
              </b-col>
            </b-row>

            <!-- BUSCADOR -->
            <b-row class="mx-2 my-3">
              <b-col cols="12">
                <b-form-input
                    id="filter-input"
                    v-model="filter"
                    type="search"
                    placeholder="Buscar por dominio, nombre o correo..."
                >
                </b-form-input>
              </b-col>
            </b-row>

            <!-- TABLA -->
            <div class="mx-2">
              <b-table 
                  class="table data-table"
                  striped
                  hover
                  outlined
                  :items="reportes.items"
                  :fields="reportes.headers"
                  :current-page="currentPage"
                  :per-page="perPage"
                  :filter="filter"
                  :filter-included-fields="filterOn"
                  :busy="loading"
                  stacked="md"
                  show-empty
                  small
              >
                <!-- LOADING -->
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle mr-2"></b-spinner>
                    <strong>Cargando datos...</strong>
                  </div>
                </template>

                <!-- EMPTY -->
                <template #empty="scope">
                  <h5 class="text-center">
                    {{ scope.emptyText='Por el momento no hay registros que mostrar.' }}
                  </h5>
                </template>

                <!-- EMPTY FILTERED -->
                <template #emptyfiltered="scope">
                  <h5 class="text-center">
                    {{ scope.emptyFilteredText='No hay registros que concuerden con los parámetros de búsqueda.' }}
                  </h5>
                </template>

                <!-- CUSTOM CELL: Dominio -->
                <template #cell(dominio)="row">
                  <b-badge variant="primary">{{ row.item.dominio }}</b-badge>
                </template>

                <!-- CUSTOM CELL: Correo -->
                <template #cell(correo_institucional)="row">
                  <span class="text-info">{{ row.item.correo_institucional }}</span>
                </template>

                <!-- CUSTOM CELL: Fecha -->
                <template #cell(ultima_conexion)="row">
                  <b-badge variant="success">{{ row.item.ultima_conexion }}</b-badge>
                </template>

                <!-- CUSTOM CELL: Estatus -->
                <template #cell(estatus)="row">
                  <b-badge variant="success">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-check-circle-fill mr-1" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                    {{ row.item.estatus }}
                  </b-badge>
                </template>

              </b-table>

              <!-- PAGINATION & EXPORT -->
              <div class="d-flex justify-content-between row p-2">
                <div class="col-sm-12 col-md-6 d-flex align-items-center">
                  <b-button 
                      @click="exportar()" 
                      variant="outline-primary" 
                      class="btn-studioAPP" 
                      size="sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-filetype-csv" viewBox="0 0 16 16">
                      <path fill-rule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5L14 4.5ZM3.517 14.841a1.13 1.13 0 0 0 .401.823c.13.108.289.192.478.252.19.061.411.091.665.091.338 0 .624-.053.859-.158.236-.105.416-.252.539-.44.125-.189.187-.408.187-.656 0-.224-.045-.41-.134-.56a1.001 1.001 0 0 0-.375-.357 2.027 2.027 0 0 0-.566-.21l-.621-.144a.97.97 0 0 1-.404-.176.37.37 0 0 1-.144-.299c0-.156.062-.284.185-.384.125-.101.296-.152.512-.152.143 0 .266.023.37.068a.624.624 0 0 1 .246.181.56.56 0 0 1 .12.258h.75a1.092 1.092 0 0 0-.2-.566 1.21 1.21 0 0 0-.5-.41 1.813 1.813 0 0 0-.78-.152c-.293 0-.551.05-.776.15-.225.099-.4.24-.527.421-.127.182-.19.395-.19.639 0 .201.04.376.122.524.082.149.2.27.352.367.152.095.332.167.539.213l.618.144c.207.049.361.113.463.193a.387.387 0 0 1 .152.326.505.505 0 0 1-.085.29.559.559 0 0 1-.255.193c-.111.047-.249.07-.413.07-.117 0-.223-.013-.32-.04a.838.838 0 0 1-.248-.115.578.578 0 0 1-.255-.384h-.765ZM.806 13.693c0-.248.034-.46.102-.633a.868.868 0 0 1 .302-.399.814.814 0 0 1 .475-.137c.15 0 .283.032.398.097a.7.7 0 0 1 .272.26.85.85 0 0 1 .12.381h.765v-.072a1.33 1.33 0 0 0-.466-.964 1.441 1.441 0 0 0-.489-.272 1.838 1.838 0 0 0-.606-.097c-.356 0-.66.074-.911.223-.25.148-.44.359-.572.632-.13.274-.196.6-.196.979v.498c0 .379.064.704.193.976.131.271.322.48.572.626.25.145.554.217.914.217.293 0 .554-.055.785-.164.23-.11.414-.26.55-.454a1.27 1.27 0 0 0 .226-.674v-.076h-.764a.799.799 0 0 1-.118.363.7.7 0 0 1-.272.25.874.874 0 0 1-.401.087.845.845 0 0 1-.478-.132.833.833 0 0 1-.299-.392 1.699 1.699 0 0 1-.102-.627v-.495Zm8.239 2.238h-.953l-1.338-3.999h.917l.896 3.138h.038l.888-3.138h.879l-1.327 4Z"/>
                    </svg>
                    Exportar a CSV
                  </b-button>
                </div>
                
                <div class="col-sm-12 col-md-6 d-flex align-items-center justify-content-end">
                  <div>
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        align="fill"
                        size="sm"
                        class="my-0"
                    >
                    </b-pagination>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import toast from "../../../mixins/ToastMixin.js";
import Bugsnag from "@bugsnag/js";

export default {
  name: "ReportesAccesoUltimaConexion",
  mixins: [toast],
  data() {
    return {
      loading: false,
      
      // FILTROS
      filtros: {
        escuela: { id: 0, label: 'Todas las escuelas' },
        escuelas_items: [],
        fecha_inicio: null,
        fecha_fin: null
      },

      // DATOS DE LA TABLA
      reportes: {
        items: [],
        headers: [
          { label: 'Dominio', key: 'dominio', class: 'text-start', sortable: true },
          { label: 'Nombre', key: 'nombre', class: 'text-start', sortable: true },
          { label: 'Correo Institucional', key: 'correo_institucional', class: 'text-start', sortable: true },
          { label: 'Última Conexión', key: 'ultima_conexion', class: 'text-center', sortable: true },
          { label: 'Estatus', key: 'estatus', class: 'text-center', sortable: true }
        ]
      },

      // PAGINACIÓN
      totalRows: 1,
      currentPage: 1,
      perPage: 15,
      pageOptions: [10, 15, 25, 50, { value: 100, text: "Ver muchos" }],
      filter: null,
      filterOn: []
    }
  },
  
  mounted() {
    this.getData();
    this.getEscuelas();
  },

  methods: {
    async getData() {
      try {
        this.loading = true;
        
        let params = {
          escuela_id: this.filtros.escuela.id || 0,
          fecha_inicio: this.filtros.fecha_inicio || '',
          fecha_fin: this.filtros.fecha_fin || ''
        };

        let response = await this.$api.get(`/api/reportes/acceso-ultima-conexion`, { params });
        
        let { success, message, color, reportes, cantidad } = response.data;
        
        if (success) {
          this.reportes.items = reportes;
          this.totalRows = cantidad;
          this.toast('b-toaster-bottom-center', true, message, color);
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.toast('b-toaster-bottom-center', true, 'Ocurrió un error al cargar los datos 🙏', "danger");
        Bugsnag.notify(new Error('Método: getData() ' + error.message));
      }
    },

    async getEscuelas() {
      try {
        let response = await this.$api.get(`/api/reportes/escuelas`);
        let { success, escuelas } = response.data;
        
        if (success) {
          this.filtros.escuelas_items = escuelas;
        }
      } catch (error) {
        Bugsnag.notify(new Error('Método: getEscuelas() ' + error.message));
      }
    },

    aplicarFiltros() {
      this.getData();
    },

    limpiarFiltros() {
      this.filtros.escuela = { id: 0, label: 'Todas las escuelas' };
      this.filtros.fecha_inicio = null;
      this.filtros.fecha_fin = null;
      this.filter = null;
      this.getData();
    },

    exportar() {
      let params = new URLSearchParams({
        escuela_id: this.filtros.escuela.id || 0,
        fecha_inicio: this.filtros.fecha_inicio || '',
        fecha_fin: this.filtros.fecha_fin || ''
      });

      const url = `${process.env.VUE_APP_API_URL}/api/reportes/acceso-ultima-conexion/exportar?${params.toString()}`;
      window.open(url, '_blank');
    }
  }
}
</script>

<style scoped>
.btn-studioAPP {
  transition: all 0.3s ease;
}

.btn-studioAPP:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}
</style>